import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    GoogleAnalyticsService,
    LocalStorageService,
} from '@interacta-shared/data-access-common';
import { ErrorService } from '@interacta-shared/data-access-error';
import { TipService } from '@interacta-shared/feature-tip';
import { filterMap, isDefined } from '@interacta-shared/util';
import {
    CustomFieldInputValue,
    ICustomFieldDefinition,
} from '@interacta/util-community';
import { GenericPost } from '@interacta/util-post';
import { dashboardCommunity } from '@modules/app-routing/routes';
import { defaultPostLinkFilters } from '@modules/communities/models/post-link-and-relation/post-link-and-relation.utils';
import { RelatedLinkedBy } from '@modules/communities/models/related-item/related-item.model';
import { CommunityActions, PostActionsApi } from '@modules/communities/store';
import { selectInitializedQuickFilters } from '@modules/communities/store/community/community.selectors';
import { changeFilters } from '@modules/communities/store/post/post.actions';
import { AppState } from '@modules/communities/store/post/post.reducer';
import { ActualizeFiltersService } from '@modules/post-filters/services/actualize-filters.service';
import {
    PostRelationsActions,
    PostRelationsApiActions,
} from '@modules/post-relations';
import { IOrder } from '@modules/post/models/filter-post/filter-post.model';
import {
    applyFiltersConstraintsToOrderClause,
    emptyPostFilters,
} from '@modules/post/models/filter-post/filter-post.utils';
import { PostListService } from '@modules/post/services/post-list.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';
import { RelationsDialogActions } from '.';
import { activityAddon } from '../relation-dialog.config';
import {
    computeRelationDialogSelectablePostsFilter,
    getRelatedItemStorageId,
} from '../relations-dialog.utils';
import * as RelationsDialogSelectors from './relations-dialog.selectors';

const orderStorageKey = 'relations-order';

@Injectable()
export class RelationsDialogEffects {
    private actions = inject(Actions);
    private store = inject(Store<AppState>);
    private postListService = inject(PostListService);
    private router = inject(Router);
    private tipService = inject(TipService);
    private errorService = inject(ErrorService);
    private localStorageService = inject(LocalStorageService);
    private actualizeFiltersService = inject(ActualizeFiltersService);
    private readonly gaService = inject(GoogleAnalyticsService);

    fetchInverseLinksOnLinkPage$ = createEffect(() =>
        this.actions.pipe(
            ofType(
                RelationsDialogActions.changePage,
                RelationsDialogActions.open,
            ),
            filter(({ page }) => page === 'link'),
            concatLatestFrom(() => [
                this.store.select(RelationsDialogSelectors.item),
                this.store.select(RelationsDialogSelectors.sourcePost),
            ]),
            filterMap(([action, itemInStore, sourcePost]) => {
                const item = action.item ?? itemInStore;

                if (item) {
                    const apiFilters =
                        computeRelationDialogSelectablePostsFilter(
                            item,
                            sourcePost,
                        );
                    if (apiFilters) {
                        return {
                            item,
                            apiFilters,
                        };
                    }
                }
                return undefined;
            }),
            map(({ item, apiFilters }) =>
                RelationsDialogActions.fetchInverseLinks({
                    item,
                    apiFilters,
                    filters: emptyPostFilters(),
                }),
            ),
        ),
    );

    fetchInverseLinks$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.fetchInverseLinks),
            switchMap(({ item, filters, apiFilters }) =>
                this.postListService
                    .getInverseRelationSelectablePosts(
                        apiFilters,
                        filters,
                        true,
                    )
                    .pipe(
                        map((posts) =>
                            RelationsDialogActions.fetchInverseLinksSuccess({
                                item,
                                posts,
                                pageToken: filters.pageToken,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                RelationsDialogActions.fetchInverseLinksError({
                                    item,
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    linkToPosts$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.linkToPosts),
            map(({ sourcePost, posts, field }) =>
                PostRelationsActions.addLinkedPosts({
                    posts: posts.map((post) => ({
                        ...post,
                        deleted: false,
                        postLinkFieldIds: [],
                        screenLinkFieldIds: [],
                    })),
                    field,
                    linkedPost: {
                        ...sourcePost,
                        deleted: false,
                        postLinkFieldIds: [],
                        screenLinkFieldIds: [],
                    },
                }),
            ),
        ),
    );

    showTipOnAddLinkedPostsSuccess$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(PostRelationsApiActions.addLinkedPostsSuccess),
                tap(() =>
                    this.tipService.success(
                        'RELATIONS_DIALOG.LINK.TIP_LINKED_SUCCESSFULLY',
                    ),
                ),
            ),
        { dispatch: false },
    );

    closeOrShowResultsOnLinkToPostsSuccess$ = createEffect(() =>
        this.actions.pipe(
            ofType(PostRelationsApiActions.addLinkedPostsSuccess),
            map(({ linksErrorConcurrency, linksErrorValidation }) =>
                linksErrorConcurrency.length === 0 &&
                linksErrorValidation.length === 0
                    ? RelationsDialogActions.close()
                    : RelationsDialogActions.changePage({
                          page: 'link-results',
                      }),
            ),
        ),
    );

    closeOnNavigation$ = createEffect(() =>
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => RelationsDialogActions.close()),
        ),
    );

    closeDialogOnPostCreated$ = createEffect(() =>
        this.actions.pipe(
            ofType(PostActionsApi.createSuccess),
            map(() => RelationsDialogActions.close()),
        ),
    );

    goToOriginCommunity$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.goToOriginCommunity),
            concatMap(({ item, post }) => this.goToOriginCommunity(item, post)),
        ),
    );

    navigateToOriginCommunity$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(RelationsDialogActions.navigateToOriginCommunity),
                tap(({ communityId }) =>
                    this.router.navigate([
                        `/${dashboardCommunity}`,
                        communityId,
                    ]),
                ),
            ),
        { dispatch: false },
    );

    changeFiltersAfterChangeQuickFilter$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.setActiveRelationQuickFilter),
            concatLatestFrom(() => [
                this.store.select(selectInitializedQuickFilters),
                this.store.select(RelationsDialogSelectors.filters),
            ]),
            mergeMap(([action, initializedQuickFilters, currentFilters]) =>
                //try to apply this function to community effects
                this.actualizeFiltersService
                    .getActualizedQuickFilters$(
                        action.quickFilter,
                        action.communityId,
                        initializedQuickFilters,
                    )
                    .pipe(
                        map((data) => ({
                            ...action,
                            ...data,
                            currentFilters,
                        })),
                        catchError((error) => {
                            this.errorService.handle(error);
                            return EMPTY;
                        }),
                    ),
            ),
            mergeMap(
                ({
                    quickFilter,
                    communityId,
                    selectedItem,
                    setInitialized,
                    sourcePostId,
                    postDefinition,
                    currentFilters,
                }) => {
                    const actions = [
                        RelationsDialogActions.changeFilters({
                            sourcePostId,
                            selectedItem,
                            postDefinition,
                            filters: {
                                ...defaultPostLinkFilters(),
                                ...quickFilter?.filters,
                                //communityId,
                                activity: postDefinition?.activityPostEnabled
                                    ? {
                                          ...quickFilter?.filters.activity,
                                          postHierarchyLevels:
                                              currentFilters.activity
                                                  ?.postHierarchyLevels,
                                      }
                                    : null,
                                archived: currentFilters.archived,
                            },
                            resetQuickFilters: false,
                        }),
                        setInitialized && quickFilter
                            ? CommunityActions.setInitializedQuickFilters({
                                  quickFilter,
                                  communityId,
                              })
                            : null,
                    ].filter(isDefined);

                    return from(actions);
                },
            ),
        ),
    );

    applyOrderConstraintsOnChangeFilters$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.changeFilters),
            concatLatestFrom(() =>
                this.store.select(RelationsDialogSelectors.filters),
            ),
            map(([action, previousFilters]) =>
                RelationsDialogActions.changeFiltersSuccess({
                    ...action,
                    filters: {
                        ...action.filters,
                        order: applyFiltersConstraintsToOrderClause(
                            previousFilters,
                            action.filters,
                            action.selectedItem.linkedBy.community,
                        ),
                    },
                }),
            ),
        ),
    );

    resetQuickFilterOnChangeFilters$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.changeFiltersSuccess),
            filter(({ resetQuickFilters }) => resetQuickFilters),
            map(({ selectedItem }) =>
                RelationsDialogActions.resetActiveRelationQuickFilter({
                    communityId: selectedItem.linkedBy.community.id,
                }),
            ),
        ),
    );

    fetchRelatedItemPostsOnChangeFilters$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.changeFiltersSuccess),
            map(({ filters, selectedItem, sourcePostId, postDefinition }) =>
                PostRelationsActions.fetchLinkedPosts({
                    postId: sourcePostId,
                    communityId: selectedItem.linkedBy.community.id,
                    metadata: postDefinition,
                    filters,
                }),
            ),
        ),
    );

    changeOrderRelationTable$ = createEffect(() =>
        this.actions.pipe(
            ofType(RelationsDialogActions.changeOrder),
            concatLatestFrom(() =>
                this.store.select(RelationsDialogSelectors.filters),
            ),
            tap(([{ order, selectedItem, sourcePostCommunityId }]) =>
                this.setCachedOrder(selectedItem, order, sourcePostCommunityId),
            ),
            map(
                ([
                    { order, sourcePostId, postDefinition, selectedItem },
                    filters,
                ]) =>
                    RelationsDialogActions.changeFilters({
                        filters: {
                            ...filters,
                            order,
                            pageToken: undefined,
                        },
                        sourcePostId,
                        selectedItem,
                        postDefinition,
                        resetQuickFilters: false,
                    }),
            ),
        ),
    );

    //########## ANALYTICS ##########
    sendAnalyticOnlinkToPosts$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(RelationsDialogActions.linkToPosts),
                tap(() =>
                    this.gaService.sendCustomEvent(
                        'isp_web_relations_post_linked',
                    ),
                ),
            ),
        { dispatch: false },
    );

    notificationError$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(RelationsDialogActions.fetchInverseLinksError),
                tap(({ error }) => this.errorService.handle(error)),
            ),
        { dispatch: false },
    );

    private setCachedOrder(
        relatedItem: RelatedLinkedBy,
        order: IOrder,
        sourcePostCommunityId: number,
    ) {
        const cachedOrders: Record<string, IOrder> | null =
            this.localStorageService.getEntry(orderStorageKey) ?? null;

        this.localStorageService.setEntry(orderStorageKey, {
            ...cachedOrders,
            [getRelatedItemStorageId(relatedItem, sourcePostCommunityId)]:
                order,
        });
    }

    private goToOriginCommunity(item: RelatedLinkedBy, post: GenericPost) {
        const customFields = (
            item.linkedBy.postLinkFieldDefinitions ?? []
        ).filter((f) => !f.deleted);
        const screenFields = (
            item.linkedBy.screenLinkFieldDefinitions ?? []
        ).filter((f) => !f.deleted);

        if (customFields.length || screenFields.length) {
            const customFieldsSearchable = customFields.filter(
                (f) => f.searchable,
            );
            const screenFieldsSearchable = screenFields.filter(
                (f) => f.searchable,
            );

            const searchableFieldsCount =
                customFieldsSearchable.length + screenFieldsSearchable.length;

            if (searchableFieldsCount === 0) {
                this.tipService.warn(
                    'RELATIONS_DIALOG.TABLE.CUSTOM_FIELD_NOT_SEARCHABLE',
                );
            } else if (searchableFieldsCount > 1) {
                this.tipService.warn(
                    `RELATIONS_DIALOG.TABLE.${activityAddon(item.linkedBy.community.type)}TOO_MANY_CUSTOM_FIELDS`,
                );
            } else {
                return from([
                    changeFilters({
                        fetchType: 'dashboard',
                        updatedFilters: {
                            ...emptyPostFilters(),
                            communityId: item.linkedBy.community.id,
                            customFields: this.toComputeCustomFieldInputValues(
                                customFieldsSearchable,
                                post,
                            ),
                            screenFields: this.toComputeCustomFieldInputValues(
                                screenFieldsSearchable,
                                post,
                            ),
                        },
                        forcePostFetch: true,
                    }),
                    RelationsDialogActions.navigateToOriginCommunity({
                        communityId: item.linkedBy.community.id,
                    }),
                ]);
            }
        }
        return EMPTY;
    }

    private toComputeCustomFieldInputValues(
        fields: ICustomFieldDefinition[],
        post: GenericPost,
    ): CustomFieldInputValue[] | undefined {
        return fields.length
            ? fields.map((definition) => ({
                  definition,
                  value: [post],
              }))
            : undefined;
    }
}
